import { useAnalytics } from '@/hooks/useAnalytics';
import setPath from '@/lib/setPath';
import { putWorkflowSnapshot } from '@/redux/actions/gravityActions';
import { workflowIsDraft as workflowIsDraftSelector } from '@/redux/selectors/workflowSelectors';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import { compareDesc } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import SnapshotsDrawer from './SnapshotsDrawer';

const WorkflowSnapshotsProvider = ({ drawerKey = 'snapshots' }) => {
  const { trackEvent } = useAnalytics();
  const workflowId = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.id);
  const activeWorkflowSnapshots = useSelector((state) => workflowScreenSelectors.activeWorkflowSnapshots(state));
  const workflowIsDraft = useSelector((state) => workflowIsDraftSelector(state, workflowId));

  const processedSnapshots = useMemo(() => {
    if (!activeWorkflowSnapshots) return [];

    return activeWorkflowSnapshots
      .filter((snapshot) => (workflowIsDraft ? snapshot.version !== 'draft' : true))
      .sort((a, b) => {
        if (a?.version === 'draft') return -1;
        if (b?.version === 'draft') return 1;
        return compareDesc(new Date(a?.createdOn), new Date(b?.createdOn));
      });
  }, [activeWorkflowSnapshots, workflowIsDraft]);

  const handleOpenInEditor = useCallback(
    (snapshot, isDraft) => {
      trackEvent('workflow-drawer-switchSnapshot');
      return new Promise((resolve, reject) => {
        putWorkflowSnapshot({ workflowId, snapshotId: snapshot?.id, replace: true, isDraft }, (response) => {
          if (response?.success !== false) {
            setPath(`/workflows/${workflowId}/draft`);
            resolve(response);
          } else {
            reject(new Error(response?.error || 'Failed to open in editor'));
          }
        });
      });
    },
    [workflowId, trackEvent]
  );

  const handlePublish = useCallback(
    (snapshot) => {
      trackEvent('workflow-drawer-publishSnapshot');
      return new Promise((resolve, reject) => {
        putWorkflowSnapshot({ workflowId, snapshotId: snapshot?.id, snapshot: { isPublished: true }, replace: false }, (response) => {
          if (response?.success !== false) {
            resolve(response);
          } else {
            reject(new Error(response?.error || 'Failed to publish snapshot'));
          }
        });
      });
    },
    [workflowId, trackEvent]
  );

  const handleUpdate = useCallback(
    (snapshot, updatedData) => {
      return new Promise((resolve, reject) => {
        putWorkflowSnapshot({ workflowId, snapshotId: snapshot?.id, snapshot: updatedData }, (response) => {
          if (response?.success !== false) {
            resolve(response);
          } else {
            reject(new Error(response?.error || 'Failed to update snapshot'));
          }
        });
      });
    },
    [workflowId]
  );

  const customActions = useMemo(
    () => ({
      onOpenInEditor: handleOpenInEditor,
      onPublish: handlePublish,
      onUpdate: handleUpdate
    }),
    [handleOpenInEditor, handlePublish, handleUpdate]
  );

  return <SnapshotsDrawer drawerKey={drawerKey} title="Workflow Snapshots" snapshots={processedSnapshots} isDraft={workflowIsDraft} customActions={customActions} className="snapshots-drawer workflow-snapshots-drawer" />;
};

export default WorkflowSnapshotsProvider;
